import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Shirt, Loader2, PackageCheck, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ConfirmDialog, EmptyState, Field, PeralatanBadge, PeralatanMeta } from "../../components/common";
import { AuthImage } from "../../components/AuthImage";
import { portalApi } from "../../lib/portalApi";
import { errMsg } from "../../lib/api";
import { fmtDate, rp, todayISO } from "../../lib/format";
import { fileToJpeg } from "../../lib/image";

const KONDISI = [["baik", "Baik"], ["rusak", "Rusak"], ["hilang", "Hilang"]];

const ItemDialog = ({ open, initial, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({ nama_barang: initial?.nama_barang || "", jumlah: String(initial?.jumlah || 1), tanggal_pinjam: initial?.tanggal_pinjam || todayISO(), catatan: initial?.catatan || "", foto: null });
  }, [open, initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const pickFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { set("foto", await fileToJpeg(file)); } catch (err) { toast.error(err.message); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.nama_barang.trim()) return toast.error("Nama barang wajib diisi");
    const jumlah = parseInt(f.jumlah, 10);
    if (!(jumlah >= 1)) return toast.error("Jumlah minimal 1");
    setSaving(true);
    try {
      const body = { nama_barang: f.nama_barang.trim(), jumlah, tanggal_pinjam: f.tanggal_pinjam, catatan: f.catatan.trim() || null, foto: f.foto };
      if (initial) await portalApi.put(`/portal/peralatan/${initial.id}`, body);
      else await portalApi.post("/portal/peralatan", body);
      toast.success(initial ? "Pinjaman diperbarui" : "Pinjaman dicatat, menunggu cek admin");
      onClose();
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portal-peralatan-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah pinjaman" : "Tambah Pinjaman"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-[1fr_88px] gap-3">
            <Field label="Nama barang"><Input data-testid="portal-peralatan-nama" className="h-10" placeholder="mis. Dress merah" value={f.nama_barang || ""} onChange={(e) => set("nama_barang", e.target.value)} /></Field>
            <Field label="Jumlah"><Input data-testid="portal-peralatan-jumlah" inputMode="numeric" className="h-10 text-center font-mono" value={f.jumlah || ""} onChange={(e) => set("jumlah", e.target.value.replace(/\D/g, "").slice(0, 3))} /></Field>
          </div>
          <Field label="Tanggal pinjam"><Input type="date" data-testid="portal-peralatan-tanggal" className="h-10" value={f.tanggal_pinjam || ""} onChange={(e) => set("tanggal_pinjam", e.target.value)} /></Field>
          <Field label="Catatan (opsional)"><Input data-testid="portal-peralatan-catatan" className="h-10" value={f.catatan || ""} onChange={(e) => set("catatan", e.target.value)} /></Field>
          <Field label="Foto barang (opsional)">
            <label className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50">
              <ImagePlus className="h-4 w-4" />{f.foto ? "Foto dipilih — ganti" : "Ambil / pilih foto"}
              <input data-testid="portal-peralatan-foto" type="file" accept="image/*" className="hidden" onChange={pickFoto} />
            </label>
            {f.foto && <img src={f.foto} alt="" className="mt-2 h-24 w-24 rounded-lg object-cover" />}
          </Field>
          <DialogFooter><Button data-testid="portal-peralatan-save" type="submit" disabled={saving} className="h-10 w-full rounded-full">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LaporDialog = ({ item, onClose, onSaved }) => {
  const [tgl, setTgl] = useState(todayISO());
  const [kondisi, setKondisi] = useState("baik");
  const [saving, setSaving] = useState(false);
  if (!item) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await portalApi.post(`/portal/peralatan/${item.id}/lapor-kembali`, { tanggal_kembali: tgl, kondisi });
      toast.success("Laporan dikirim, menunggu cek admin");
      onClose();
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="portal-lapor-dialog">
        <DialogHeader><DialogTitle className="font-heading">Lapor Dikembalikan: {item.nama_barang}</DialogTitle></DialogHeader>
        <Field label="Tanggal kembali"><Input type="date" data-testid="portal-lapor-tanggal" className="h-10" value={tgl} onChange={(e) => setTgl(e.target.value)} /></Field>
        <Field label="Kondisi">
          <div className="grid grid-cols-3 gap-2">
            {KONDISI.map(([v, l]) => (
              <button key={v} type="button" data-testid={`portal-lapor-kondisi-${v}`} onClick={() => setKondisi(v)}
                className={`h-10 rounded-lg border text-sm font-semibold ${kondisi === v ? "border-primary bg-primary text-white" : "border-gray-200 text-gray-700"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <DialogFooter><Button data-testid="portal-lapor-save" disabled={saving || !tgl} onClick={submit} className="h-10 w-full rounded-full">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kirim laporan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ItemCard = ({ p, onEdit, onDelete, onLapor }) => {
  const editable = p.status_tampil === "menunggu" && p.cek_jenis === "pinjam";
  return (
    <div data-testid={`portal-peralatan-${p.id}`} className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        {p.has_foto && <AuthImage client={portalApi} src={`/portal/peralatan/${p.id}/foto`} className="h-14 w-14 shrink-0 rounded-lg" />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">{p.nama_barang}{p.jumlah > 1 && <span className="font-mono text-gray-500"> ×{p.jumlah}</span>}</p>
          <p className="text-xs text-gray-500">Pinjam {fmtDate(p.tanggal_pinjam)}{p.tanggal_kembali ? ` · kembali ${fmtDate(p.tanggal_kembali)}` : ""}{p.kondisi ? ` · ${p.kondisi}` : ""}</p>
          {p.cek_jenis === "kembali" && p.laporan_kembali && <p className="text-xs text-sky-700">Lapor kembali {fmtDate(p.laporan_kembali.tanggal_kembali)} ({p.laporan_kembali.kondisi}) — menunggu cek</p>}
          {p.biaya ? <p className="text-xs font-mono text-gray-700">Biaya {rp(p.biaya)}{p.keterangan_biaya ? ` (${p.keterangan_biaya})` : ""}</p> : null}
          {p.alasan_tolak && <p data-testid={`portal-peralatan-alasan-${p.id}`} className="text-xs text-red-600">{p.alasan_tolak}</p>}
          <PeralatanMeta p={p} />
        </div>
        <PeralatanBadge status={p.status_tampil} testid={`portal-peralatan-status-${p.id}`} />
      </div>
      {(editable || (p.status_tampil === "dipinjam")) && (
        <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
          {editable ? (
            <>
              <Button data-testid={`portal-peralatan-edit-${p.id}`} variant="outline" className="h-9 flex-1" onClick={onEdit}><Pencil className="mr-1.5 h-4 w-4" />Ubah</Button>
              <Button data-testid={`portal-peralatan-delete-${p.id}`} variant="outline" className="h-9 text-red-600" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>
            </>
          ) : (
            <Button data-testid={`portal-peralatan-lapor-${p.id}`} variant="outline" className="h-9 flex-1" onClick={onLapor}><PackageCheck className="mr-1.5 h-4 w-4" />Lapor Dikembalikan</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default function PortalPeralatan() {
  const [items, setItems] = useState(null);
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [lapor, setLapor] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => portalApi.get("/portal/peralatan").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const doDelete = async () => {
    try { await portalApi.delete(`/portal/peralatan/${del.id}`); toast.success("Pinjaman dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-gray-900">Peralatan saya</h2>
        <Button data-testid="portal-peralatan-add" className="h-9 rounded-full" onClick={() => setDlg({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" />Tambah Pinjaman</Button>
      </div>
      {items && items.length === 0 ? <EmptyState icon={Shirt} title="Belum ada pinjaman" desc="Catat barang yang Anda pinjam dari agency." /> : (
        <div className="space-y-3" data-testid="portal-peralatan-list">
          {(items || []).map((p) => <ItemCard key={p.id} p={p} onEdit={() => setDlg({ open: true, item: p })} onDelete={() => setDel(p)} onLapor={() => setLapor(p)} />)}
        </div>
      )}
      <ItemDialog open={dlg.open} initial={dlg.item} onClose={() => setDlg({ open: false, item: null })} onSaved={load} />
      <LaporDialog item={lapor} onClose={() => setLapor(null)} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title="Hapus pinjaman?" description={del?.nama_barang} confirmLabel="Hapus" onConfirm={doDelete} testid="portal-peralatan-delete-confirm" />
    </div>
  );
}
